import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Sun, Moon, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'sonner';
import { authAPI } from '../../services/api';

const LeftPanel = () => (
  <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
    <img
      src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwc2VjdXJpdHklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTQ4Mjc1fDA&ixlib=rb-4.1.0&q=85"
      alt="Data Security"
      className="absolute inset-0 w-full h-full object-cover opacity-60"
    />
    <div className="relative z-10 flex flex-col justify-between p-12 text-white">
      <div className="flex items-center gap-3">
        <img src="/fulllogo_transparent.png" alt="Assura" className="h-10 brightness-0 invert" />
      </div>
      <div>
        <h1 className="text-4xl font-bold mb-4">Compliance Without Complexity</h1>
        <p className="text-lg text-slate-300">
          Simplify compliance, manage risk, and protect data across your organization with our
          comprehensive Caribbean data protection compliance platform.
        </p>
      </div>
      <p className="text-sm text-slate-400">© 2026 Assura. All rights reserved.</p>
    </div>
  </div>
);

export default function VerifyEmailChangePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    verifyToken();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found. Please use the link sent to your new email address.');
      return;
    }

    try {
      const response = await authAPI.verifyEmailChange(token);
      setNewEmail(response.data.new_email || '');
      setStatus('success');
      toast.success('Email address verified successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed. The link may have expired or already been used.';
      setStatus('error');
      setErrorMessage(detail);
    }
  };

  const ThemeToggle = () => (
    <div className="absolute top-4 right-4">
      <Button variant="ghost" size="icon" onClick={toggleTheme}>
        {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>
    </div>
  );

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verifying your email address...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex">
        <LeftPanel />
        <div className="flex-1 flex items-center justify-center p-8 bg-background">
          <ThemeToggle />
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-2">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-2xl font-bold">Email Verified</CardTitle>
              <CardDescription>
                Your email address has been updated successfully.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {newEmail && (
                <div className="p-3 bg-green-50 dark:bg-green-950 rounded-md border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200 text-center">
                    You can now log in with <strong>{newEmail}</strong>
                  </p>
                </div>
              )}
              <p className="text-sm text-muted-foreground text-center">
                Your account has been unlocked. Please log in with your new email address.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex">
      <LeftPanel />
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <ThemeToggle />
        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <CardTitle className="text-2xl font-bold">Verification Failed</CardTitle>
            <CardDescription>
              We could not verify your email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-800 dark:text-red-200">{errorMessage}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator to resend the verification email or cancel the email change.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full" variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
